import React from "react";
import { createPortal } from "../../windowManager/browserFenster/fensterPortal";
import {
  Form, MarkierAktion, MarkierZustand, MARKIER_START, Werkzeug, markierReducer,
  TEXT_FAMILIE, textGroesse, textSchrift, trefferText, verschiebeForm, zeichneFormen,
} from "./markierung";
import { alleFenster } from "../../windowManager/browserFenster/fensterRegister";

const WERKZEUGE: Array<{ w: Werkzeug; label: string }> = [
  { w: "rahmen", label: "Rahmen" }, { w: "pfeil", label: "Pfeil" }, { w: "stift", label: "Stift" },
  { w: "text", label: "Text" }, { w: "schwaerzen", label: "Schwärzen" },
];

type Props = { bild: HTMLCanvasElement; onFertig: (png: Blob) => void; onAbbruch: () => void };

/** Offenes Textfeld: neuer Text (index null) oder ein vorhandener, der gerade bearbeitet wird. */
type TextEdit = { index: number | null; x: number; y: number; text: string };
/** Laufendes Verschieben eines Texts: Startpunkt und bisheriger Versatz in Bildpixeln. */
type Zug = { index: number; sx: number; sy: number; dx: number; dy: number };

export function MarkierEditor({ bild, onFertig, onAbbruch }: Props) {
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const [werkzeug, setWerkzeug] = React.useState<Werkzeug>("rahmen");
  const [aktiv, setAktiv] = React.useState<Form | null>(null);
  const linie = Math.max(2, Math.round(bild.width / 400));

  // Zustand + Refs: Tasten-/Blur-Handler und „Fertig" brauchen den aktuellen
  // Stand synchron, nicht den des letzten Renders.
  const [zustand, setZustand] = React.useState<MarkierZustand>(MARKIER_START);
  const zustandRef = React.useRef(zustand);
  const anwenden = React.useCallback((a: MarkierAktion): MarkierZustand => {
    const neu = markierReducer(zustandRef.current, a);
    zustandRef.current = neu;
    setZustand(neu);
    return neu;
  }, []);
  const formen = zustand.formen;

  const [edit, setEdit] = React.useState<TextEdit | null>(null);
  const editRef = React.useRef<TextEdit | null>(null);
  const setzeEdit = (e: TextEdit | null) => { editRef.current = e; setEdit(e); };

  const [zug, setZug] = React.useState<Zug | null>(null);
  const zugRef = React.useRef<Zug | null>(null);
  const setzeZug = (z: Zug | null) => { zugRef.current = z; setZug(z); };

  /** Anzeigegröße / Bildgröße — das Canvas wird per CSS skaliert, das Textfeld muss mitskalieren. */
  const [skala, setSkala] = React.useState(1);
  React.useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const messe = () => setSkala(c.clientWidth ? c.clientWidth / bild.width : 1);
    messe();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(messe);
    ro.observe(c);
    return () => ro.disconnect();
  }, [bild]);

  /** Textmaße in Bildpixeln, mit derselben Schrift wie beim Einbrennen. */
  const messen = React.useCallback((text: string) => {
    const ctx = canvasRef.current?.getContext("2d");
    const h = textGroesse(linie) * 1.2;
    if (!ctx) return { b: text.length * textGroesse(linie) * 0.6, h };
    ctx.save();
    ctx.font = textSchrift(linie);
    const b = ctx.measureText(text).width;
    ctx.restore();
    return { b, h };
  }, [linie]);
  const greifrand = linie * 2;

  const zeichnen = React.useCallback((liste: Form[]) => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d")!;
    ctx.clearRect(0, 0, c.width, c.height);
    ctx.drawImage(bild, 0, 0);
    zeichneFormen(ctx, liste, linie);
  }, [bild, linie]);

  // Live-Ansicht: gezogener Text an neuer Stelle, bearbeiteter Text ausgeblendet
  // (das Textfeld liegt an seiner Stelle), dazu die gerade entstehende Form.
  React.useEffect(() => {
    const liste = formen
      .map((f, i) => (zug && i === zug.index ? verschiebeForm(f, zug.dx, zug.dy) : f))
      .filter((_, i) => !(edit && edit.index === i));
    zeichnen(aktiv ? [...liste, aktiv] : liste);
  }, [formen, aktiv, zug, edit, zeichnen]);

  /** Übernimmt das offene Textfeld (leer → nichts). Idempotent: zweiter Aufruf (z. B. Blur danach) tut nichts. */
  const textUebernehmen = React.useCallback((): MarkierZustand => {
    const e = editRef.current;
    if (!e) return zustandRef.current;
    editRef.current = null;
    setEdit(null);
    if (e.index !== null) return anwenden({ typ: "aendern", index: e.index, text: e.text });
    const text = e.text.trim();
    return text ? anwenden({ typ: "hinzu", form: { w: "text", x: e.x, y: e.y, text } }) : zustandRef.current;
  }, [anwenden]);

  const textVerwerfen = () => setzeEdit(null);

  const rueckgaengig = React.useCallback(() => {
    // Bei offenem Textfeld heißt „Rückgängig": diesen Text verwerfen.
    if (editRef.current) { setzeEdit(null); return; }
    anwenden({ typ: "rueckgaengig" });
  }, [anwenden]);

  // onAbbruch in einer Ref halten: der Tasten-Effekt darf nur EINMAL beim
  // Mount registrieren. Stünde onAbbruch direkt in den deps und übergibt der
  // Aufrufer ein Inline-Arrow, würde der Listener bei jedem Render neu
  // an- und abgemeldet — unnötig, aber vor allem riskant bei schnellen
  // Tastenfolgen zwischen Ab- und Wieder-Anmelden.
  const onAbbruchRef = React.useRef(onAbbruch);
  React.useEffect(() => {
    onAbbruchRef.current = onAbbruch;
  });

  React.useEffect(() => {
    const taste = (e: KeyboardEvent) => {
      // Offenes Textfeld: Escape/Enter/Strg+Z gehören dem Feld (dessen
      // onKeyDown verwirft/übernimmt bzw. macht das Tippen rückgängig) —
      // hier weder den ganzen Editor schließen noch eine Form entfernen.
      if (editRef.current) return;
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z") {
        e.preventDefault();
        e.stopPropagation();
        rueckgaengig();
      } else if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        onAbbruchRef.current();
      }
    };
    // Capture-Phase: läuft VOR den Bubbling-Handlern eines umschließenden
    // Dialogs, damit Escape/Ctrl+Z hier abgefangen werden und nicht bis zu
    // dessen Tasten-Handler durchlaufen (z. B. ein Dialog-Escape, das das
    // ganze Fenster schließt, während nur der Editor abbrechen soll).
    alleFenster.addEventListener("keydown", taste, true);
    return () => alleFenster.removeEventListener("keydown", taste, true);
  }, [rueckgaengig]);

  /** Zeigerposition → Bildpixel (Canvas wird per CSS skaliert). */
  const punkt = (e: { clientX: number; clientY: number }): [number, number] => {
    const r = canvasRef.current!.getBoundingClientRect();
    return [((e.clientX - r.left) / r.width) * bild.width, ((e.clientY - r.top) / r.height) * bild.height];
  };
  const textUnter = (x: number, y: number) => trefferText(zustandRef.current.formen, x, y, messen, greifrand);

  const runter = (e: React.PointerEvent<HTMLCanvasElement>) => {
    // Kein Fokuswechsel aufs Canvas/Body: sonst verlöre ein gerade geöffnetes Textfeld sofort den Fokus.
    e.preventDefault();
    // Klick neben ein offenes Textfeld übernimmt es — und fängt nichts Neues an.
    if (editRef.current) { textUebernehmen(); return; }
    const [x, y] = punkt(e);
    e.currentTarget.setPointerCapture?.(e.pointerId);
    // Auf einem Text: verschieben, egal welches Werkzeug gewählt ist.
    const i = textUnter(x, y);
    if (i >= 0) { setzeZug({ index: i, sx: x, sy: y, dx: 0, dy: 0 }); return; }
    if (werkzeug === "text") { setzeEdit({ index: null, x, y, text: "" }); return; }
    if (werkzeug === "pfeil") setAktiv({ w: "pfeil", x1: x, y1: y, x2: x, y2: y });
    else if (werkzeug === "stift") setAktiv({ w: "stift", punkte: [[x, y]] });
    else setAktiv({ w: werkzeug, x, y, b: 0, h: 0 });
  };

  const bewegen = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const [x, y] = punkt(e);
    const z = zugRef.current;
    if (z) { setzeZug({ ...z, dx: x - z.sx, dy: y - z.sy }); return; }
    if (!aktiv) {
      e.currentTarget.style.cursor = textUnter(x, y) >= 0 ? "move" : werkzeug === "text" ? "text" : "";
      return;
    }
    if (aktiv.w === "pfeil") setAktiv({ ...aktiv, x2: x, y2: y });
    else if (aktiv.w === "stift") setAktiv({ ...aktiv, punkte: [...aktiv.punkte, [x, y]] });
    else if (aktiv.w === "rahmen" || aktiv.w === "schwaerzen") setAktiv({ ...aktiv, b: x - aktiv.x, h: y - aktiv.y });
  };

  const hoch = () => {
    const z = zugRef.current;
    if (z) {
      anwenden({ typ: "verschieben", index: z.index, dx: z.dx, dy: z.dy });
      setzeZug(null);
      return;
    }
    if (aktiv) anwenden({ typ: "hinzu", form: aktiv });
    setAktiv(null);
  };

  const doppelklick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const [x, y] = punkt(e);
    const i = textUnter(x, y);
    const f = zustandRef.current.formen[i];
    if (f?.w === "text") setzeEdit({ index: i, x: f.x, y: f.y, text: f.text });
  };

  const fertig = () => {
    // Offenes Textfeld zuerst übernehmen; eine gerade gezogene Form gehört
    // mit aufs Bild — wie in der Live-Vorschau.
    const s = textUebernehmen();
    zeichnen(aktiv ? [...s.formen, aktiv] : s.formen);
    canvasRef.current!.toBlob((b) => { if (b) onFertig(b); }, "image/png");
  };

  const schrift = textGroesse(linie) * skala;

  return createPortal(
    <div className="kvp-rd tk-markier" data-ticket-ignore>
      <div className="tk-markier-leiste">
        <div className="rd-seg">
          {WERKZEUGE.map((x) => (
            <button key={x.w} type="button" className={werkzeug === x.w ? "active" : ""} onClick={() => setWerkzeug(x.w)}>{x.label}</button>
          ))}
        </div>
        <button
          type="button"
          className="tp-detail-btn"
          disabled={!zustand.verlauf.length && !edit}
          // Fokus im Textfeld lassen, damit Rückgängig es verwirft statt es erst zu übernehmen.
          onMouseDown={(e) => e.preventDefault()}
          onClick={rueckgaengig}
        >Rückgängig</button>
        <span className="tk-flex" />
        <button type="button" className="tp-detail-btn" onClick={onAbbruch}>Verwerfen</button>
        <button type="button" className="cd-save" onClick={fertig}>Fertig</button>
      </div>
      <div className="tk-markier-flaeche">
        <div className="tk-markier-buehne">
          <canvas
            ref={canvasRef}
            width={bild.width}
            height={bild.height}
            className="tk-markier-canvas"
            onPointerDown={runter}
            onPointerMove={bewegen}
            onPointerUp={hoch}
            onPointerCancel={hoch}
            onMouseDown={(e) => e.preventDefault()}
            onDoubleClick={doppelklick}
          />
          {edit && (
            <input
              autoFocus
              className="tk-markier-text"
              aria-label="Text"
              value={edit.text}
              onChange={(e) => setzeEdit({ ...edit, text: e.target.value })}
              onBlur={() => textUebernehmen()}
              onKeyDown={(e) => {
                // Tasten im Feld bleiben im Feld (kein Dialog-Escape, kein Editor-Rückgängig).
                e.stopPropagation();
                if (e.key === "Enter") { e.preventDefault(); textUebernehmen(); }
                else if (e.key === "Escape") { e.preventDefault(); textVerwerfen(); }
              }}
              style={{
                left: `${(edit.x / bild.width) * 100}%`,
                top: `${(edit.y / bild.height) * 100}%`,
                fontSize: schrift,
                fontWeight: 600,
                fontFamily: TEXT_FAMILIE,
                width: Math.max(messen(edit.text).b * skala + schrift, schrift * 4),
              }}
            />
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
